import './_accountMenu.scss'

import * as paths from 'utils/Links'

import { Badge, Box, Menu, MenuItem, Typography } from '@mui/material'
import { useAuthService } from 'api/AuthServiceConfig'
import { startTransition, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { menuItems, menuSubitems } from 'utils/LinksMenu'
import UseApiRequest from './UseApiRequest'

const AccountMenu = (): JSX.Element => {
  const { authState } = useAuthService()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const { notifications, firstName, getNotificationsSummary, getFirstName } = UseApiRequest()

  const navigate = useNavigate()
  const location = useLocation()

  const isAuthenticated = authState.status === 'authenticated'

  useEffect(() => {
    if (isAuthenticated) {
      getNotificationsSummary()
      getFirstName()
    }
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Closes the menu urgently and navigates as a transition, so the close is never held up behind the
   * destination.
   *
   * Left in one batch, the menu stayed open for as long as the destination took to arrive: portal routes
   * are `lazy`, so a first visit suspends while its chunk downloads, and React parks the whole batch —
   * the close with it — until that resolves. `Menu`'s backdrop is invisible but still takes pointer
   * events, so everything underneath was unclickable for the length of the download. Measured on a cold
   * chunk at over 9s against 0.35s warm; on CI it outlasts Cypress's actionability timeout and fails the
   * notifications journey. Marking only the navigation as a transition leaves `setAnchorEl` urgent, so it
   * commits immediately; `navigate` still updates history synchronously, so nothing waiting on the URL
   * has to wait for the chunk.
   */
  const handleMenuClick = (pageURL: string, serverRedirect?: boolean) => {
    setAnchorEl(null)

    if (serverRedirect) {
      paths.serverRedirect(pageURL)
    } else {
      startTransition(() => navigate(pageURL))
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    open ? setAnchorEl(null) : setAnchorEl(e.currentTarget)
  }

  const currentPath = location.pathname

  return (
    <>
      <ul className="AccountMenu AppMenuBar" data-test="account-menu-bar">
        <li className={`${anchorEl && 'anchorEl-active'}`}>
          <Typography onClick={handleMenu} data-test="account-menu-icon">
            <span>
              <span data-test="user-info-name" className="userInfoName">
                {isAuthenticated ? firstName : ''}
              </span>
              <Badge badgeContent={notifications} color="error">
                <span className="TopMenuIcon lw-marketing-icons-user"></span>
              </Badge>
            </span>
          </Typography>

          <Menu
            className="TopbarMenu TopbarMenu-manIcon"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {menuItems.map((menuItem, index) => {
              const { menuTitle, pageURL, badge, serverRedirect } = menuItem
              return (
                <MenuItem
                  data-test={menuItem.testDescription}
                  onClick={() => handleMenuClick(pageURL, serverRedirect)}
                  key={index}
                  selected={currentPath === pageURL}
                >
                  <a
                    className="MuiLink-withoutUnderline menu-cta"
                    href={pageURL}
                    onClick={(e) => {
                      e.preventDefault()
                    }}
                  >
                    {menuTitle}
                    {badge && (
                      <Box component="span" ml={8}>
                        <Badge badgeContent={notifications} color="error" />
                      </Box>
                    )}
                  </a>
                </MenuItem>
              )
            })}

            <div className="menuSeparateLine"></div>

            {menuSubitems.map((menuItem, index) => {
              const { menuTitle, pageURL, icon } = menuItem
              return (
                <MenuItem
                  data-test={menuItem.testDescription}
                  onClick={() => handleMenuClick(pageURL, false)}
                  key={index}
                  selected={currentPath === pageURL}
                >
                  <a
                    className="MuiLink-withoutUnderline menu-cta"
                    href={pageURL}
                    onClick={(e) => {
                      e.preventDefault()
                    }}
                  >
                    {icon && <Box component="span" mr={1} className={`icons-colorBlack icons-fs-16 ${icon}`}></Box>}
                    {menuTitle}
                  </a>
                </MenuItem>
              )
            })}
          </Menu>
        </li>
      </ul>
    </>
  )
}

export default AccountMenu
